import './universalLink.css';

import React, { useEffect, useMemo, useState } from 'react';
import { ErrorFallback, ErrorPage, LoadingPage } from '@bentley/pw-components';
import { SvgProjectWise } from '@bentley/pw-file-icons';
import { Text } from '@itwin/itwinui-react';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useUsageLogging } from '../../hooks/useUsageLogging';
import { t } from '../../services/translation';
import { useCannedConnection } from './data/useCannedConnection';
import { useSolutionType } from './data/useSolutionType';
import { UniversalLinkHeaderFooter } from './headerFooter/universalLinkHeaderFooter';
import { PWELink } from './pweLink/pweLink';
import { useAutoOpenPWE } from './pweLink/useAutoOpenPWE';
import { readUniversalLinkParams } from './searchParams/searchParams';
import { TeamsLink } from './teamsLink/teamsLink';
import { useAutoOpenWeb } from './webLink/useAutoOpenWeb';
import { WebLink } from './webLink/webLink';
import { useAutoOpenWebView } from './webViewLink/useAutoOpenWebView';
import { WebViewLink } from './webViewLink/webViewLink';

export function UniversalLink(): JSX.Element {
  const user = useAuthentication({ allowUnauthenticated: true });
  const solutionType = useSolutionType();
  const cannedConnection = useCannedConnection();

  useAutoOpenPWE(cannedConnection);
  useAutoOpenWeb(solutionType);
  useAutoOpenWebView(cannedConnection);

  const logUsage = useUsageLogging();

  const [showLoading, setShowLoading] = useState<boolean>(true);

  const pweLink = PWELink({ cannedConnection });
  const webLink = WebLink({ solutionType });
  const webViewLink = WebViewLink({ cannedConnection });
  const teamsLink = TeamsLink();

  const initialized = useMemo((): boolean => {
    if (user === undefined) {
      return false;
    }
    if (solutionType === undefined || cannedConnection === undefined) {
      return false;
    }

    return true;
  }, [cannedConnection, user, solutionType]);

  const invalidLink = useMemo((): boolean => {
    const {
      datasource,
      objectId,
      objectType,
      savedSearchId,
      workAreaId,
      projectId,
      app
    } = readUniversalLinkParams();

    if (!pweLink && !webLink && !webViewLink && !teamsLink) {
      return true;
    }

    const canResolveWebLink = workAreaId && projectId;
    if (app == 'web' && !canResolveWebLink) {
      return true;
    }

    const canResolveItemLink = (objectId && objectType) || savedSearchId;
    if (!datasource || !canResolveItemLink) {
      return true;
    }

    if (objectId && objectType && savedSearchId) {
      return true;
    }

    if (app == 'pwe' && savedSearchId) {
      return true;
    }

    return false;
  }, [pweLink, teamsLink, webLink, webViewLink]);

  useEffect(() => {
    logUsage('PROJECTWISE_LINKS');
    document.title = t('SharedLinks.ProjectWiseLink');
  }, [t, logUsage]);

  useEffect(() => {
    if (initialized) {
      // Delay turning off load screen to prevent flash on auto-redirect
      setTimeout(() => setShowLoading(false), 500);
    }
  }, [initialized]);

  if (showLoading) {
    return (
      <ErrorFallback>
        <LoadingPage loadingSubText={t('SharedLinks.ResolvingLink')} />
      </ErrorFallback>
    );
  }

  if (invalidLink) {
    return (
      <ErrorFallback>
        <UniversalLinkHeaderFooter user={user}>
          <main className="universal-link">
            <ErrorPage
              errorType="404"
              error={t('SharedLink.PWLinkNotFound')}
              errorDescription={t('SharedLinks.CouldNotResolveLink')}
            />
          </main>
        </UniversalLinkHeaderFooter>
      </ErrorFallback>
    );
  }

  return (
    <ErrorFallback>
      <UniversalLinkHeaderFooter user={user}>
        <main className="universal-link">
          <Text variant="headline">{t('SharedLinks.HowToOpenLink')}</Text>
          <SvgProjectWise className="pw-logo" />
          <Text variant="subheading">{t('SharedLinks.SelectDestination')}</Text>
          <span className="link-row">
            {pweLink}
            {webLink}
            {webViewLink}
            {teamsLink}
          </span>
        </main>
      </UniversalLinkHeaderFooter>
    </ErrorFallback>
  );
}
